import React, {ReactNode} from 'react'
import {css} from '@emotion/core'
import styled from '@emotion/styled'
import isPropValid from '@emotion/is-prop-valid'
import {MdCheck as Check} from 'react-icons/md'

import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'

const LG_INPUT_HEIGHT = 56

export const baseLayout = ({theme, textTransformType}) => css`
  ${theme.typography.text.input};
  display: inline-flex;
  position: relative;
  outline: none;
  width: 100%;
  border: none;
  border-radius: ${theme.layout.radius.md}px;
  color: ${theme.colors.textInputDefault};
  background: ${theme.colors.textInputBg};
  padding: ${theme.layout.spacing.s400}px ${theme.layout.spacing.s500}px;
  text-transform: ${textTransformType};
  /* For IE 11 support */
  height: 1rem;

  &::placeholder {
    color: ${theme.colors.textInputPlaceholder};
  }

  &:disabled {
    color: ${theme.colors.midnight.m300};
    border-color: ${theme.colors.midnight.m200};
    background-color: ${theme.colors.borderColor};
  }

  &:focus,
  &:active {
    border-color: ${theme.colors.inputBorderFocused};
  }
`

export const withLabel = ({isSelectEmpty}) =>
  !isSelectEmpty &&
  css`
    &:focus + label,
    &:not([value='']):not(textarea) + label,
    &:not(:empty) + label {
      transform: scale(0.7);
      transform-origin: top left;
      transition: transform 200ms;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `

export const withSize = ({theme, size}) => {
  switch (size) {
    case 'lg':
      return css`
        min-height: ${LG_INPUT_HEIGHT}px;
      `
    case 'md':
    default:
      return css`
        min-height: ${theme.layout.spacing.s900}px;
      `
  }
}

export const withBorder = ({theme, withBorder, isActive}) =>
  withBorder &&
  css`
    border: 1px solid ${theme.colors.inputBorder};
    ${isActive &&
    css`
      border-color: ${theme.colors.inputBorderFocused};
    `}
  `

export const withIcon = ({theme, hasEndIcon, showValidIcon}) => {
  const iconPadding = hasEndIcon ? theme.layout.spacing.s800 : 0
  const validIconPadding = showValidIcon ? theme.layout.spacing.s800 : 0

  if (hasEndIcon || showValidIcon)
    return css`
      padding-right: ${iconPadding + validIconPadding}px;
    `
}

export const withAnimatedPlaceholder = ({theme, isPlaceholderAnimated}) =>
  isPlaceholderAnimated &&
  css`
    padding-top: ${theme.layout.spacing.s600}px;
  `

export const withIconPrependedStyles = ({theme, isIconPrepended}) =>
  isIconPrepended &&
  css`
    padding-left: ${theme.layout.spacing.s800}px;
  `

export const InputContainer = styled('div')`
  position: relative;
`

export const InputWrapper = styled('div')(
  () => css`
    position: relative;
  `
)

export const invalidState = ({theme, isInvalid}) =>
  isInvalid &&
  css`
    border-color: ${theme.colors.inputValidationErrorColor};
  `

export const IconWrapper = styled('div')(
  ({theme}) => css`
    margin-left: ${theme.layout.spacing.s300}px;
  `
)

export const IconWrapperPrepended = styled('div')(
  () => css`
    margin-left: 0;
  `
)

// Stop iconsWrapper from capturing clicks, but make descendant buttons or anchors clickable
const withClickableIcon = () => css`
  pointer-events: none;

  & a,
  & button {
    pointer-events: auto;
  }
`

export const IconsWrapper = styled('div')(
  ({theme}) => css`
    position: absolute;
    height: 100%;
    display: flex;
    right: ${theme.layout.spacing.s500}px;
    top: 0px;
    align-items: center;
  `,
  withClickableIcon
)

export const IconsWrapperPrepended = styled('div')(
  ({theme}) => css`
    position: absolute;
    height: 100%;
    display: flex;
    left: ${theme.layout.spacing.s500}px;
    top: 0px;
    align-items: center;
  `,
  withClickableIcon
)

export const AnimatedPlaceholder = styled('label')(
  ({theme}) => css`
    width: calc(100% - ${theme.layout.spacing.s700}px);
    overflow: hidden;
    transform-origin: top left;
    transition: transform 200ms;
    white-space: nowrap;
    text-overflow: ellipsis;
    position: absolute;
    pointer-events: none;
    top: 0;
    transform: translateY(15%);
    font-weight: 500;
    padding: ${theme.layout.spacing.s400}px 0;
    left: ${theme.layout.spacing.s500}px;
    font-family: ${theme.fonts.family.default};
    color: ${theme.colors.inputLabelColor};
    font-size: ${theme.fonts.size.md};
  `
)

export const ValidIcon = styled(Check, {
  shouldForwardProp: isPropValid
})(
  ({theme}) => css`
    color: ${theme.colors.inputValidationPassColor};
  `
)

export const VerifiedLabel = styled('span')(
  ({theme}) => css`
    font-size: ${theme.fonts.size.xs};
    font-weight: ${theme.fonts.weight.lg};
    color: ${theme.colors.inputVerifiedColor};
  `
)

export const ValidationMessageElement = styled('div')(
  ({theme}) => css`
    ${theme.typography.text.caption};
    padding: ${theme.layout.spacing.s200}px ${theme.layout.spacing.s500}px;
    color: ${theme.colors.inputValidationErrorColor};
  `
)

export const getIsPlaceholderAnimated = (
  withAnimatedPlaceholder: boolean,
  size?: string
): boolean =>
  size ? withAnimatedPlaceholder && size === 'lg' : withAnimatedPlaceholder

export const getShowValidIcon = (
  withValidationIcon: boolean,
  isTouched: boolean,
  hasError: boolean
): boolean => withValidationIcon && isTouched && !hasError

export const getShowErrorMessage = (
  isTouched: boolean,
  hasError: boolean,
  errorMessage: string
): boolean => isTouched && hasError && Boolean(errorMessage)

export const getClassName = (isPrivate: boolean, className?: string) =>
  isPrivate ? (className ? `fs-mask ${className}` : 'fs-mask') : className

const getOptionalLabel = (displayName: string | ReactNode) => (
  <FormattedMessageWrapper
    id="optionalLabel"
    defaultMessage="{displayName} (optional)"
    values={{
      displayName
    }}
  />
)

const getRequiredLabel = (displayName: string | ReactNode) => (
  <FormattedMessageWrapper
    id="requiredLabel"
    defaultMessage="{displayName} *"
    values={{
      displayName
    }}
  />
)

export const formatLabel = (
  placeholder: string | ReactNode,
  requiredFieldFlag?: boolean,
  optionalFieldFlag?: boolean
) => {
  if (optionalFieldFlag) {
    return getOptionalLabel(placeholder)
  }

  if (requiredFieldFlag) {
    return getRequiredLabel(placeholder)
  }

  return placeholder
}
